import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, User, Users } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { useEnterpriseBeneficiaryDetail } from "../hooks/useEnterpriseBeneficiaryDetail";

type EnterpriseBeneficiaryDetailPageProps = {
  type: "employee" | "dependant";
  beneficiaryId: number;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR");
}

function formatGender(value: string | null | undefined): string {
  if (value === "M") return "Masculin";
  if (value === "F") return "Féminin";
  return value || "—";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function EnterpriseBeneficiaryDetailPage({
  type,
  beneficiaryId,
}: EnterpriseBeneficiaryDetailPageProps) {
  if (type === "employee") {
    return <Navigate to={`/dashboard/employees/${beneficiaryId}`} replace />;
  }

  const { beneficiary, loading, error } = useEnterpriseBeneficiaryDetail(
    type,
    beneficiaryId,
  );

  if (loading) {
    return (
      <div className="theme-card p-8 text-center text-sm text-muted">
        Chargement du bénéficiaire…
      </div>
    );
  }

  if (!beneficiary) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">{error ?? "Bénéficiaire introuvable."}</p>
        <Link
          to="/dashboard/beneficiaries"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={14} />
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref="/dashboard/enterprise"
          items={[
            { label: "Gestion employés" },
            { label: "Liste bénéficiaires", href: "/dashboard/beneficiaries" },
            { label: beneficiary.name },
          ]}
        />
        <Link
          to="/dashboard/beneficiaries"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={15} />
          Retour
        </Link>
      </div>

      <div className="theme-card p-6">
        <div className="mb-6 flex flex-wrap items-start gap-4">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-soft text-lg font-semibold text-purple">
            {getInitials(beneficiary.name)}
          </span>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{beneficiary.name}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <StatusBadge variant="purple">{beneficiary.relation}</StatusBadge>
              <StatusBadge variant="purple">{beneficiary.matricule}</StatusBadge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <section>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <User size={16} className="text-primary" />
              Informations
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Date de naissance</dt>
                <dd className="mt-1 text-foreground">{formatDate(beneficiary.birthdate)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Sexe</dt>
                <dd className="mt-1 text-foreground">{formatGender(beneficiary.gender)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Matricule entreprise</dt>
                <dd className="mt-1 text-foreground">{beneficiary.company_matricule || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Entreprise</dt>
                <dd className="mt-1 text-foreground">{beneficiary.company || "—"}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <Users size={16} className="text-primary" />
              Agent rattaché
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Nom</dt>
                <dd className="mt-1 text-foreground">
                  {beneficiary.employee_id ? (
                    <Link
                      to={`/dashboard/employees/${beneficiary.employee_id}`}
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      {beneficiary.employee_name}
                    </Link>
                  ) : (
                    beneficiary.employee_name || "—"
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">ID système</dt>
                <dd className="mt-1 text-foreground">{beneficiary.matricule || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Poste</dt>
                <dd className="mt-1 text-foreground">{beneficiary.position || "—"}</dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </>
  );
}
